import type { IComponentController } from 'angular'

import { app } from '../../../app.module'

// 相関グラフ設定
export interface CorrelationSetting {
  object: string
  yAxis: {
    value: string
    text: string
  }
  xAxis: {
    value: string
    text: string
  }
  zAxis: {
    value: string
    text: string
  }
  viewCountType: string
  dataPerPostFlg: boolean
}

/**
 * 相関グラフ設定クラス
 * @class AnalyticsCorrelationSettingCtrl
 */
class AnalyticsCorrelationSettingCtrl implements IComponentController {
  // SNSタイプ
  private snsType: string
  // 対象
  private objectList: {
    value: string
    text: string
  }[]
  private object: string
  // 軸一覧
  private axisList: {
    value: string
    text: string
  }[]
  // Y軸
  private yAxis: string
  // X軸
  private xAxis: string
  // Z軸(円大きさ)
  private zAxis: string
  // 表示件数
  private viewCountType: string
  private viewCountTypeList: {
    value: string
    text: string
  }[]
  // 1投稿あたりの平均
  private dataPerPostFlg: boolean
  // グラフ設定
  private graphSetting: CorrelationSetting
  // グラフ設定の変更イベント
  private onChangeSetting: Function

  /**
   * グラフ設定をセット
   */
  public setGraphSetting(): void {
    const xAxis = this.axisList.filter((axis) => {
      return axis.value === this.xAxis
    })[0]
    const yAxis = this.axisList.filter((axis) => {
      return axis.value === this.yAxis
    })[0]
    const zAxis = this.axisList.filter((axis) => {
      return axis.value === this.zAxis
    })[0]

    this.graphSetting = {
      object: this.object,
      xAxis: xAxis,
      yAxis: yAxis,
      zAxis: zAxis,
      viewCountType: this.viewCountType,
      dataPerPostFlg: this.dataPerPostFlg,
    }
    // グラフ設定の変更イベント
    this.onChangeSetting({ graphSetting: this.graphSetting })
  }

  /**
   * 初期化
   */
  public $onInit(): void {
    // 対象の初期化
    this.initObject()
    // X, Y, Z軸の初期化
    this.initAxis()
    // 表示件数の初期化
    this.initViewCountType()
    // 1投稿あたりの平均の初期化
    this.dataPerPostFlg = true
    // グラフ設定をセット
    this.setGraphSetting()
  }

  /**
   * 対象の初期化
   * @private
   * @return {void}
   */
  private initObject(): void {
    this.objectList = [
      {
        value: 'account',
        text: 'アカウント',
      },
      {
        value: 'brandCategory',
        text: '大カテゴリ',
      },
      {
        value: 'brandSubCategory',
        text: '小カテゴリ',
      },
    ]
    this.object = 'account'
  }

  /**
   * X, Y, Z軸の初期化
   * @private
   * @return {void}
   */
  private initAxis(): void {
    switch (this.snsType) {
      case 'facebook':
        this.axisList = [
          {
            value: 'engagementRate',
            text: '反応率',
          },
          {
            value: 'engagements',
            text: '反応数',
          },
          {
            value: 'likes',
            text: 'いいね！',
          },
          {
            value: 'comments',
            text: 'コメント',
          },
          {
            value: 'shares',
            text: 'シェア',
          },
          {
            value: 'followersUpdownRate',
            text: 'フォロワー増減',
          },
          {
            value: 'followers',
            text: 'フォロワー数',
          },
          {
            value: 'posts',
            text: '投稿数',
          },
        ]
        this.xAxis = 'engagementRate'
        this.yAxis = 'followers'
        this.zAxis = 'posts'
        break
      case 'twitter':
        this.axisList = [
          {
            value: 'engagementRate',
            text: '反応率',
          },
          {
            value: 'engagements',
            text: '反応数',
          },
          {
            value: 'impressions',
            text: 'インプレッション',
          },
          {
            value: 'retweets',
            text: 'リポスト',
          },
          {
            value: 'quotes',
            text: '引用',
          },
          {
            value: 'likes',
            text: 'いいね',
          },
          {
            value: 'replies',
            text: '返信',
          },
          {
            value: 'followersUpdownRate',
            text: 'フォロワー増減',
          },
          {
            value: 'followers',
            text: 'フォロワー数',
          },
          {
            value: 'posts',
            text: '投稿数',
          },
        ]
        this.xAxis = 'engagementRate'
        this.yAxis = 'followers'
        this.zAxis = 'posts'
        break
      case 'instagram':
        this.axisList = [
          {
            value: 'engagementRate',
            text: '反応率',
          },
          {
            value: 'engagements',
            text: '反応数',
          },
          {
            value: 'likes',
            text: 'いいね！',
          },
          {
            value: 'comments',
            text: 'コメント',
          },
          {
            value: 'followersUpdownRate',
            text: 'フォロワー増減',
          },
          {
            value: 'followers',
            text: 'フォロワー数',
          },
          {
            value: 'posts',
            text: '投稿数',
          },
        ]
        this.xAxis = 'engagementRate'
        this.yAxis = 'followers'
        this.zAxis = 'posts'
        break
      case 'youtube':
        this.axisList = [
          {
            value: 'views',
            text: '視聴回数',
          },
          {
            value: 'likes',
            text: '高く評価',
          },
          {
            value: 'dislikes',
            text: '低く評価',
          },
          {
            value: 'comments',
            text: 'コメント',
          },
          {
            value: 'durations',
            text: '動画秒数',
          },
          {
            value: 'followersUpdownRate',
            text: 'フォロワー増減',
          },
          {
            value: 'followers',
            text: 'フォロワー数',
          },
          {
            value: 'posts',
            text: '動画数',
          },
        ]
        this.xAxis = 'views'
        this.yAxis = 'followers'
        this.zAxis = 'posts'
        break
      case 'line':
        this.axisList = [
          {
            value: 'engagementRate',
            text: '反応率',
          },
          {
            value: 'engagements',
            text: '反応数',
          },
          {
            value: 'likes',
            text: 'いいね',
          },
          {
            value: 'comments',
            text: 'コメント',
          },
          {
            value: 'shares',
            text: 'シェア',
          },
          {
            value: 'followersUpdownRate',
            text: 'フォロワー増減',
          },
          {
            value: 'followers',
            text: 'フォロワー数',
          },
          {
            value: 'posts',
            text: '投稿数',
          },
        ]
        this.xAxis = 'engagementRate'
        this.yAxis = 'followers'
        this.zAxis = 'posts'
        break
    }
  }

  /**
   * 表示件数の初期化
   */
  private initViewCountType(): void {
    this.viewCountTypeList = [
      {
        value: 'yAxis-10',
        text: 'Y軸上位10件',
      },
      {
        value: 'yAxis-25',
        text: 'Y軸上位25件',
      },
      {
        value: 'yAxis-50',
        text: 'Y軸上位50件',
      },
      {
        value: 'yAxis-100',
        text: 'Y軸上位100件',
      },
      {
        value: 'xAxis-10',
        text: 'X軸上位10件',
      },
      {
        value: 'xAxis-25',
        text: 'X軸上位25件',
      },
      {
        value: 'xAxis-50',
        text: 'X軸上位50件',
      },
      {
        value: 'xAxis-100',
        text: 'X軸上位100件',
      },
      {
        value: 'zAxis-10',
        text: '円の大きさ上位10件',
      },
      {
        value: 'zAxis-25',
        text: '円の大きさ上位25件',
      },
      {
        value: 'zAxis-50',
        text: '円の大きさ上位50件',
      },
      {
        value: 'zAxis-100',
        text: '円の大きさ上位100件',
      },
    ]
    this.viewCountType = 'yAxis-10'
  }
}

app.component('analyticsCorrelationSetting', {
  bindings: {
    snsType: '<',
    onChangeSetting: '&',
  },
  template: require('./analytics-correlation-setting.pug')(),
  controller: AnalyticsCorrelationSettingCtrl,
})
